import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Divider,
    useTheme,
    InputAdornment,
    Menu,
    Tooltip,
    Avatar,
    Alert,
    Snackbar,
    FormControlLabel,
    Switch,
    Checkbox,
    FormGroup
} from '@mui/material';
import {
    Delete,
    Search,
    Add as AddIcon,
    Filter as FilterIcon,
    Refresh as RefreshIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    PictureAsPdf,
    TableChart,
    GetApp,
    CheckCircle,
    ExitToApp,
    CurrencyExchange,
    MoneyOff,
    Checklist,
    Download,
    Build,
    ReceiptLong as ReceiptIconLong,
    Print as PrintIcon,
    PictureAsPdf as PdfIcon,
    Calculate as CalculateIcon,
    CleaningServices as CleaningIcon,
    Construction as ConstructionIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIPropertyList,
    APIAssignmentList,
    APIMoveOutList,
    APIMoveOutStore,
    APIMoveOutUpdate,
    APIMoveOutDelete,
    APIMoveOutExportExcel,
    APIMoveOutExportPDF,
    APIMoveOutPrint,
    APIMoveOutStats,
    APIMoveOutInspection,
    APIMoveOutGenerateStatement,
    APIMoveOutClose,
    APIMoveOutCalculate
} from '../../apiServices/APIs';
import { formatCreatedAtDate, recordsCount, recordsPerPage } from '../../utils/helpers';
import { getUserDetails } from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import ErrorState from '../../components/ErrorrState';
import LoadingState from '../../components/LoadingState';
import { usePermission } from '../../hooks/usePermission';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const MoveOutChecklist = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [updateId, setUpdateId] = useState('');
    const [postStatus, setPostStatus] = useState('ADD');
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [pageState, setPageState] = useState({
        pageData: [],
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statistics, setStatistics] = useState(null);
    const [properties, setProperties] = useState([]);
    const [activeTenants, setActiveTenants] = useState([]);
    const [selectedTenant, setSelectedTenant] = useState('');
    const [calculatedData, setCalculatedData] = useState(null);
    const [inspectionDialogOpen, setInspectionDialogOpen] = useState(false);
    const [inspectionItems, setInspectionItems] = useState([]);
    const [damageCharges, setDamageCharges] = useState(0);
    const [damageNotes, setDamageNotes] = useState('');
    const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
    const [closeDialogOpen, setCloseDialogOpen] = useState(false);
    const [closeNotes, setCloseNotes] = useState('');

    const moveOutStatusOptions = [
        { id: 'initiated', name: 'Initiated', color: 'info', icon: <ExitToApp />, step: 1 },
        { id: 'inspection_done', name: 'Inspection Done', color: 'warning', icon: <Checklist />, step: 2 },
        { id: 'statement_generated', name: 'Statement Generated', color: 'primary', icon: <ReceiptIconLong />, step: 3 },
        { id: 'closed', name: 'Closed', color: 'success', icon: <CheckCircle />, step: 4 }
    ];

    const inspectionChecklist = [
        { id: 'walls', label: 'Walls - No damage/holes', category: 'structural' },
        { id: 'floors', label: 'Floors - Clean, no damage', category: 'structural' },
        { id: 'ceilings', label: 'Ceilings - No stains/damage', category: 'structural' },
        { id: 'doors', label: 'Doors - Working properly', category: 'fixtures' },
        { id: 'windows', label: 'Windows - Intact, working locks', category: 'fixtures' },
        { id: 'plumbing', label: 'Plumbing - No leaks, working fixtures', category: 'plumbing' },
        { id: 'electrical', label: 'Electrical - All outlets/switches working', category: 'electrical' },
        { id: 'appliances', label: 'Appliances - Clean and working', category: 'appliances' },
        { id: 'cabinets', label: 'Cabinets - Intact, clean', category: 'fixtures' },
        { id: 'countertops', label: 'Countertops - No damage/stains', category: 'fixtures' },
        { id: 'bathroom', label: 'Bathroom - Clean, working fixtures', category: 'plumbing' },
        { id: 'hvac', label: 'HVAC - Working properly', category: 'hvac' },
        { id: 'smoke_detectors', label: 'Smoke Detectors - Working', category: 'safety' },
        { id: 'paint', label: 'Paint - No excessive marks', category: 'cosmetic' },
        { id: 'cleanliness', label: 'Overall Cleanliness', category: 'cleaning' }
    ];

    const [formData, setFormData] = useState({
        tenant_id: '',
        property_id: '',
        floor_number: '',
        move_out_date: new Date().toISOString().split('T')[0],
        last_month_rent_due: '',
        security_deposit_held: '',
        damage_charges: '',
        unpaid_rent_balance: '',
        final_amount_refund: '',
        final_amount_owed_by_tenant: '',
        move_out_status: 'initiated',
        notes: '',
        forwarding_address: '',
        key_returned: false,
        key_returned_date: '',
        cleaning_fee: '',
        repair_costs: '',
        inspection_notes: ''
    });

    const [filters, setFilters] = useState({
        move_out_status: '',
        property_id: '',
        date_from: '',
        date_to: ''
    });

    const [filterOptions, setFilterOptions] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    const { hasPermission, loading: permissionLoading } = usePermission();

    useEffect(() => {
        apiService.setToastFunction(showToast);
        fetchProperties();
        fetchActiveTenants();
        fetchMoveOuts();
        fetchStatistics();
    }, [showToast]);

    useEffect(() => {
        if (openAdd && (postStatus === "ADD" || postStatus === "UPDATE")) {
            const securityHeld = parseFloat(formData.security_deposit_held) || 0;
            const damage = parseFloat(formData.damage_charges) || 0;
            const unpaid = parseFloat(formData.unpaid_rent_balance) || 0;
            const cleaning = parseFloat(formData.cleaning_fee) || 0;
            const repairs = parseFloat(formData.repair_costs) || 0;

            const totalDeductions = damage + unpaid + cleaning + repairs;
            const refund = Math.max(0, securityHeld - totalDeductions);
            const owed = Math.max(0, totalDeductions - securityHeld);

            setFormData(prev => {
                if (prev.final_amount_refund !== refund || prev.final_amount_owed_by_tenant !== owed) {
                    return {
                        ...prev,
                        final_amount_refund: refund,
                        final_amount_owed_by_tenant: owed
                    };
                }
                return prev;
            });
        }
    }, [
        formData.security_deposit_held,
        formData.damage_charges,
        formData.unpaid_rent_balance,
        formData.cleaning_fee,
        formData.repair_costs,
        openAdd,
        postStatus
    ]);

    const fetchProperties = async () => {
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, APIPropertyList);
            if (result.success) {
                setProperties(Array.isArray(result.data) ? result.data : []);
            }
        } catch (error) {
            console.error("Error fetching properties:", error);
        }
    };

    const fetchActiveTenants = async () => {
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, APIAssignmentList, { assignment_status: 'active' });
            if (result.success) {
                const assignments = Array.isArray(result.data) ? result.data : [];
                const uniqueTenants = [...new Map(assignments.map(a => [a.tenant_id, a])).values()];
                setActiveTenants(uniqueTenants);
            }
        } catch (error) {
            console.error("Error fetching active tenants:", error);
            setActiveTenants([]);
        }
    };

    const fetchMoveOuts = async () => {
        setLoading(true);
        setError(null);
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, APIMoveOutList);

            if (result.success) {
                const moveOuts = Array.isArray(result.data) ? result.data.reverse() : [];
                setPageState(prev => ({
                    ...prev,
                    pageData: moveOuts
                }));
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError("Failed to load data");
            console.error("Fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchStatistics = async () => {
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.listStats(userBranchId, APIMoveOutStats);
            if (result.success && result.data) {
                setStatistics(result.data);
            }
        } catch (error) {
            console.error("Error fetching statistics:", error);
        }
    };

    const handleCalculate = async () => {
        const tenantId = selectedTenant || formData.tenant_id;
        if (!tenantId) {
            showToast("Validation Error", "Please select a tenant", "warning");
            return;
        }

        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.listWithParams(userBranchId, APIMoveOutCalculate, { 
                tenant_id: tenantId, 
                branch_id: userBranchId 
            });
            
            if (result.success && result.data) {
                const calculated = result.data;
                setCalculatedData(calculated);
                
                setFormData(prev => ({
                    ...prev,
                    last_month_rent_due: calculated.last_month_rent_due,
                    security_deposit_held: calculated.security_deposit_held,
                    damage_charges: calculated.damage_charges,
                    unpaid_rent_balance: calculated.unpaid_rent_balance,
                    final_amount_refund: calculated.final_amount_refund,
                    final_amount_owed_by_tenant: calculated.final_amount_owed_by_tenant
                }));
                
                showToast("Success", "Calculation completed successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to calculate amounts", "error");
            }
        } catch (error) {
            console.error("Calculation error:", error);
            showToast("Error", "Failed to calculate amounts", "error");
        }
    };

    const handleExportClick = (event) => { setExportAnchorEl(event.currentTarget); };
    const handleExportClose = () => { setExportAnchorEl(null); };
    
    const handlePrintStatement = async (record) => {
        if (!hasPermission('PRINT')) {
            showToast("Permission Denied", "You don't have permission to print statements", "error");
            return;
        }
        
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('print_id', record.id);
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            const result = await apiService.print(APIMoveOutPrint, formDataToSend);
            if (result && result.success) {
                showToast("Success", "PDF file generated successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to generate PDF file", "error");
            }
        } catch (error) {
            console.error("Print error:", error);
            showToast("Error", "Failed to generate PDF file", "error");
        }
    };
    
    const handleExportExcel = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export move-out data", "error");
            handleExportClose();
            return;
        }
        
        try {
            handleExportClose();
            showToast("Processing", "Preparing Excel export...", "info");
            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            const result = await apiService.exportToExcel(APIMoveOutExportExcel, formDataToSend);
            if (result && result.success) {
                showToast("Success", "Excel file downloaded successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to export Excel file", "error");
            }
        } catch (error) {
            console.error('Excel export error:', error);
            showToast("Error", error.message || "Failed to export Excel file", "error");
        }
    };

    const handleExportPDF = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export move-out data", "error");
            handleExportClose();
            return;
        }
        
        try {
            handleExportClose();
            showToast("Processing", "Preparing PDF export...", "info");

            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            const result = await apiService.exportToPDF(APIMoveOutExportPDF, formDataToSend);

            if (result && result.success) {
                showToast("Success", "PDF file downloaded successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to export PDF file", "error");
            }
        } catch (error) {
            console.error('PDF export error:', error);
            showToast("Error", error.message || "Failed to export PDF file", "error");
        }
    };

    const handleCompleteInspection = async () => {
        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('branch_id', getUserDetails("branchId"));
        formDataToSend.append('move_out_id', selectedRecord.id);
        formDataToSend.append('inspection_items', JSON.stringify(inspectionItems));
        formDataToSend.append('damage_charges', damageCharges);
        formDataToSend.append('damage_notes', damageNotes);

        try {
            const result = await apiService.create(APIMoveOutInspection, formDataToSend);
            if (result && result.success) {
                showToast("Success", result.message, "success");
                setInspectionDialogOpen(false);
                fetchMoveOuts();
                fetchStatistics();
            }
        } catch (error) {
            console.error("Inspection error:", error);
            showToast("Error", "Failed to save inspection", "error");
        }
    };

    const handleGenerateStatement = async () => {
        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('branch_id', getUserDetails("branchId"));
        formDataToSend.append('move_out_id', selectedRecord.id);
        formDataToSend.append('user_name', getUserDetails("userName"));

        try {
            const result = await apiService.downloadPDF(APIMoveOutGenerateStatement, formDataToSend);
            if (result && result.success) {
                showToast("Success", "Statement downloaded successfully", "success");
                setGenerateDialogOpen(false);
                // Refresh to update status
                fetchMoveOuts();
                fetchStatistics();
            } else {
                showToast("Error", result?.message || "Failed to generate statement", "error");
            }
        } catch (error) {
            console.error("Generate statement error:", error);
            showToast("Error", "Failed to generate statement", "error");
        }
    };

    const handleClose = async () => {
        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('branch_id', getUserDetails("branchId"));
        formDataToSend.append('move_out_id', selectedRecord.id);
        formDataToSend.append('close_notes', closeNotes);

        try {
            const result = await apiService.create(APIMoveOutClose, formDataToSend);
            if (result && result.success) {
                showToast("Success", result.message, "success");
                setCloseDialogOpen(false);
                setCloseNotes('');
                fetchMoveOuts();
                fetchStatistics();
            }
        } catch (error) {
            console.error("Close error:", error);
            showToast("Error", "Failed to close move-out", "error");
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const clearFilters = () => {
        setFilters({
            move_out_status: '',
            property_id: '',
            date_from: '',
            date_to: ''
        });
        setSearchTerm('');
        handleCloseOptions();
    };

    const applyFilters = () => {
        handleCloseOptions();
    };

    const handlefilterOptions = () => setFilterOptions(true);
    const handleCloseOptions = () => setFilterOptions(false);
    const handleCloseView = () => setOpenView(false);
    const handleOpenView = (record) => {
        setSelectedRecord(record);
        setOpenView(true);
    };

    const handleOpenAdd = () => {
        if (!hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to create move-out records", "error");
            return;
        }

        setPostStatus("ADD");
        setFormData({
            tenant_id: '',
            property_id: '',
            floor_number: '',
            move_out_date: new Date().toISOString().split('T')[0],
            last_month_rent_due: '',
            security_deposit_held: '',
            damage_charges: '',
            unpaid_rent_balance: '',
            final_amount_refund: '',
            final_amount_owed_by_tenant: '',
            move_out_status: 'initiated',
            notes: '',
            forwarding_address: '',
            key_returned: false,
            key_returned_date: '',
            cleaning_fee: '',
            repair_costs: '',
            inspection_notes: ''
        });
        setCalculatedData(null);
        setSelectedTenant('');
        setOpenAdd(true);
    };

    const handleCloseAdd = () => {
        setOpenAdd(false);
        setSelectedTenant('');
        setCalculatedData(null);
    };

    const handleOpenEdit = (record) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to edit move-out records", "error");
            return;
        }

        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("UPDATE");
        setSelectedTenant(record.tenant_id || '');

        const currentTenantExists = activeTenants.some(
            t => (t.tenant_id || t.id)?.toString() === record.tenant_id?.toString()
        );

        if (!currentTenantExists && record.tenant_id) {
            const recordTenant = {
                tenant_id: record.tenant_id,
                tenant_name: record.tenant_name,
                property_id: record.property_id,
                floor_number: record.floor_number
            };
            setActiveTenants(prev => [recordTenant, ...prev]);
        }

        setFormData({
            tenant_id: record.tenant_id || '',
            property_id: record.property_id || '',
            floor_number: record.floor_number || '',
            move_out_date: record.move_out_date || '',
            last_month_rent_due: record.last_month_rent_due || '',
            security_deposit_held: record.security_deposit_held || '',
            damage_charges: record.damage_charges || '',
            unpaid_rent_balance: record.unpaid_rent_balance || '',
            final_amount_refund: record.final_amount_refund || '',
            final_amount_owed_by_tenant: record.final_amount_owed_by_tenant || '',
            move_out_status: record.move_out_status || 'initiated',
            notes: record.notes || '',
            forwarding_address: record.forwarding_address || '',
            key_returned: record.key_returned || false,
            key_returned_date: record.key_returned_date || '',
            cleaning_fee: record.cleaning_fee || '',
            repair_costs: record.repair_costs || '',
            inspection_notes: record.inspection_notes || ''
        });

        setCalculatedData({
            last_month_rent_due: record.last_month_rent_due || 0,
            security_deposit_held: record.security_deposit_held || 0,
            damage_charges: record.damage_charges || 0,
            unpaid_rent_balance: record.unpaid_rent_balance || 0,
            final_amount_refund: record.final_amount_refund || 0,
            final_amount_owed_by_tenant: record.final_amount_owed_by_tenant || 0
        });

        setOpenAdd(true);
    };

    const handleOpenDelete = (record) => {
        if (!hasPermission('DELETE')) {
            showToast("Permission Denied", "You don't have permission to delete move-out records", "error");
            return;
        }

        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("DELETE");
        setOpenDelete(true);
    };

    const handleCloseDelete = () => {
        setOpenDelete(false);
    };

    const handleOpenInspection = (record) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to perform inspection", "error");
            return;
        }
        setSelectedRecord(record);
        setInspectionItems(record.inspection_checklist || []);
        setDamageCharges(record.damage_charges || 0);
        setDamageNotes(record.inspection_notes || '');
        setInspectionDialogOpen(true);
    };

    const handleOpenGenerateStatement = (record) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to generate statements", "error");
            return;
        }
        setSelectedRecord(record);
        setGenerateDialogOpen(true);
    };

    const handleOpenClose = (record) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to close move-out", "error");
            return;
        }
        setSelectedRecord(record);
        setCloseNotes('');
        setCloseDialogOpen(true);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name === 'tenant_id') {
            const selectedTenantData = activeTenants.find(
                t => (t.tenant_id || t.id)?.toString() === value?.toString()
            );
            if (selectedTenantData) {
                setSelectedTenant(value);
                setFormData(prev => ({
                    ...prev,
                    [name]: value,
                    property_id: selectedTenantData.property_id || '',
                    floor_number: selectedTenantData.floor_number || ''
                }));
            } else {
                setSelectedTenant(value);
                setFormData(prev => ({
                    ...prev,
                    [name]: value
                }));
            }
            return;
        }

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleInspectionItemToggle = (itemId) => {
        setInspectionItems(prev => 
            prev.includes(itemId) ? prev.filter(i => i !== itemId) : [...prev, itemId]
        );
    };

    const validateForm = () => {
        if (!formData.tenant_id) {
            showToast("Validation Error", "Please select a tenant", "warning");
            return false;
        }
        if (!formData.move_out_date) {
            showToast("Validation Error", "Move-out date is required", "warning");
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (postStatus === "ADD" && !hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to create move-out records", "error");
            return;
        }
        
        if (postStatus === "UPDATE" && !hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to update move-out records", "error");
            return;
        }
        
        if (postStatus === "DELETE" && !hasPermission('DELETE')) {
            showToast("Permission Denied", "You don't have permission to delete move-out records", "error");
            return;
        }
        
        if (postStatus !== "DELETE") {
            if (!validateForm()) return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('branch_id', getUserDetails("branchId"));

        if (postStatus !== "DELETE") {
            formDataToSend.append('tenant_id', formData.tenant_id);
            formDataToSend.append('property_id', formData.property_id);
            formDataToSend.append('floor_number', formData.floor_number || '');
            formDataToSend.append('move_out_date', formData.move_out_date);
            formDataToSend.append('last_month_rent_due', formData.last_month_rent_due || 0);
            formDataToSend.append('security_deposit_held', formData.security_deposit_held || 0);
            formDataToSend.append('damage_charges', formData.damage_charges || 0);
            formDataToSend.append('unpaid_rent_balance', formData.unpaid_rent_balance || 0);
            formDataToSend.append('final_amount_refund', formData.final_amount_refund || 0);
            formDataToSend.append('final_amount_owed_by_tenant', formData.final_amount_owed_by_tenant || 0);
            formDataToSend.append('move_out_status', formData.move_out_status);
            formDataToSend.append('notes', formData.notes || '');
            formDataToSend.append('forwarding_address', formData.forwarding_address || '');
            formDataToSend.append('key_returned', formData.key_returned ? 1 : 0);
            formDataToSend.append('key_returned_date', formData.key_returned_date || '');
            formDataToSend.append('cleaning_fee', formData.cleaning_fee || 0);
            formDataToSend.append('repair_costs', formData.repair_costs || 0);
            formDataToSend.append('inspection_notes', formData.inspection_notes || '');
        }

        if (postStatus === "UPDATE" || postStatus === "DELETE") {
            formDataToSend.append('updateId', updateId);
        }

        try {
            let result;
            if (postStatus === "ADD") {
                result = await apiService.create(APIMoveOutStore, formDataToSend);
            } else if (postStatus === "UPDATE") {
                result = await apiService.update(APIMoveOutUpdate, formDataToSend);
            } else if (postStatus === "DELETE") {
                result = await apiService.delete(APIMoveOutDelete, formDataToSend);
            }

            if (result && result.success) {
                showToast("Success", result.message, "success");
                handleCloseAdd();
                handleCloseDelete();
                fetchMoveOuts();
                fetchStatistics();
                fetchActiveTenants();
            }
        } catch (error) {
            console.error("API Error:", error);
        }
    };

    const getStatusColor = (status) => {
        const option = moveOutStatusOptions.find(opt => opt.id === status);
        return option?.color || 'default';
    };

    const getStatusIcon = (status) => {
        const option = moveOutStatusOptions.find(opt => opt.id === status);
        return option?.icon || <ExitToApp />;
    };

    const getStatusText = (status) => {
        const option = moveOutStatusOptions.find(opt => opt.id === status);
        return option ? option.name : status;
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-UG', {
            style: 'currency',
            currency: 'UGX',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    const filteredData = pageState.pageData.filter(record => {
        const matchesSearch = searchTerm === '' ||
            record.tenant_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            record.move_out_id?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = !filters.move_out_status || 
            record.move_out_status === filters.move_out_status;

        const matchesProperty = !filters.property_id || 
            record.property_id?.toString() === filters.property_id;

        let matchesDate = true;
        if (filters.date_from && record.move_out_date) {
            matchesDate = matchesDate && record.move_out_date >= filters.date_from;
        }
        if (filters.date_to && record.move_out_date) {
            matchesDate = matchesDate && record.move_out_date <= filters.date_to;
        }

        return matchesSearch && matchesStatus && matchesProperty && matchesDate;
    });

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    if (permissionLoading) {
        return <LoadingState message="Loading permissions..." size="large" fullPage />;
    }

    if (loading) {
        return <LoadingState message="Loading Move-Out Data..." size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchMoveOuts} title="Unable to Load Move-Out Data" fullPage retryText="Retry Loading" />;
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {statistics && (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">Active Move-Outs</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                        {statistics.active_move_outs || 0}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.primary.light, width: 56, height: 56 }}>
                                    <ExitToApp sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">Total Refunds</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                        {formatCurrency(statistics.total_refunds || 0)}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.success.light, width: 56, height: 56 }}>
                                    <CurrencyExchange sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">Total Outstanding</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                                        {formatCurrency(statistics.total_outstanding || 0)}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.warning.light, width: 56, height: 56 }}>
                                    <MoneyOff sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">Completed</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.info.main }}>
                                        {statistics.completed_move_outs || 0}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.info.light, width: 56, height: 56 }}>
                                    <CheckCircle sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, flexShrink: 0 }}>
                        Tenant Move-Out Checklist ({recordsCount(pageState.pageData.length)} Total)
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        {hasPermission('ADD') && (
                            <Button
                                variant="contained"
                                color="warning"
                                startIcon={<AddIcon />}
                                onClick={handleOpenAdd}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                New Move-Out
                            </Button>
                        )}
                        {hasPermission('VIEW') && (
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<FilterIcon />}
                                onClick={handlefilterOptions}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                Filter Options
                            </Button>
                        )}
                        {hasPermission('EXPORT') && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<GetApp />}
                                onClick={handleExportClick}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                Export Data
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            color="info"
                            startIcon={<RefreshIcon />}
                            onClick={() => { fetchMoveOuts(); fetchStatistics(); fetchActiveTenants(); }}
                            sx={{ textTransform: 'none', borderRadius: '8px' }}
                        >
                            Refresh
                        </Button>
                    </Box>

                    <Paper sx={{
                        width: '300px',
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: 'auto',
                        borderRadius: '8px',
                        background: theme.palette.primary.light
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search move-outs..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, color: 'white' }} />,
                                sx: {
                                    '& fieldset': { border: 'none' },
                                    fontSize: '0.875rem',
                                    color: 'white',
                                    '& input': { color: 'white', '&::placeholder': { color: 'rgba(255,255,255,0.7)', opacity: 1 } }
                                }
                            }}
                            size="small"
                        />
                    </Paper>
                </Box>
            </Paper>

            <Menu anchorEl={exportAnchorEl} open={Boolean(exportAnchorEl)} onClose={handleExportClose}>
                <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}><TableChart fontSize="small" color="success" /> Export to Excel (.xlsx)</MenuItem>
                <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}><PictureAsPdf fontSize="small" color="error" /> Export to PDF (.pdf)</MenuItem>
            </Menu>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: theme.palette.primary.main, '& th': { color: 'white', fontWeight: 'bold', fontSize: '0.95rem' } }}>
                                    <TableCell>#</TableCell>
                                    <TableCell>Tenant</TableCell>
                                    <TableCell>Property</TableCell>
                                    <TableCell>Move-Out Date</TableCell>
                                    <TableCell align="right">Deposit Held</TableCell>
                                    <TableCell align="right">Refund</TableCell>
                                    <TableCell align="right">Owed</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((record, index) => (
                                    <TableRow key={record.id || index} hover sx={{ '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover } }}>
                                        <TableCell><Chip label={page * rowsPerPage + index + 1} color="primary" size="small" /></TableCell>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{record.tenant_name}</Typography>
                                                <Typography variant="caption" color="text.secondary">{record.tenant_phone}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{record.property_name}</Typography>
                                        </TableCell>
                                        <TableCell>{record.move_out_date}</TableCell>
                                        <TableCell align="right">{formatCurrency(record.security_deposit_held)}</TableCell>
                                        <TableCell align="right" sx={{ color: theme.palette.success.main }}>{formatCurrency(record.final_amount_refund)}</TableCell>
                                        <TableCell align="right" sx={{ color: theme.palette.error.main }}>{formatCurrency(record.final_amount_owed_by_tenant)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={getStatusIcon(record.move_out_status)}
                                                label={getStatusText(record.move_out_status)}
                                                color={getStatusColor(record.move_out_status)}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            {hasPermission('VIEW') && (
                                                <Tooltip title="View Details"><IconButton onClick={() => handleOpenView(record)} color="primary"><ViewIcon /></IconButton></Tooltip>
                                            )}
                                            {hasPermission('PRINT') && (
                                                <Tooltip title="Print Statement"><IconButton onClick={() => handlePrintStatement(record)} color="info"><PrintIcon /></IconButton></Tooltip>
                                            )}
                                            {hasPermission('EDIT') && (
                                                <>
                                                    <Tooltip title="Edit"><IconButton onClick={() => handleOpenEdit(record)} color="secondary"><EditIcon /></IconButton></Tooltip>
                                                    {record.move_out_status === 'initiated' && (
                                                        <Tooltip title="Conduct Inspection"><IconButton onClick={() => handleOpenInspection(record)} color="warning"><Checklist /></IconButton></Tooltip>
                                                    )}
                                                    {(record.move_out_status === 'initiated' || record.move_out_status === 'inspection_done') && (
                                                        <Tooltip title="Generate Statement"><IconButton onClick={() => handleOpenGenerateStatement(record)} color="primary"><ReceiptIconLong /></IconButton></Tooltip>
                                                    )}
                                                    {record.move_out_status === 'statement_generated' && (
                                                        <Tooltip title="Close Move-Out"><IconButton onClick={() => handleOpenClose(record)} color="success"><CheckCircle /></IconButton></Tooltip>
                                                    )}
                                                </>
                                            )}
                                            {hasPermission('DELETE') && (
                                                <Tooltip title="Delete"><IconButton onClick={() => handleOpenDelete(record)} color="error"><Delete /></IconButton></Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination rowsPerPageOptions={[5, 10, 25, 50, 100]} component="div" count={filteredData.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} sx={{ mt: 2 }} />
                    {filteredData.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                            <ExitToApp sx={{ fontSize: 60, mb: 2 }} />
                            <Typography variant="h6">No move-out records found</Typography>
                        </Box>
                    )}
                </Paper>
            </motion.div>

            <Dialog 
                open={filterOptions} 
                TransitionComponent={Transition} 
                fullWidth 
                maxWidth="sm" 
                onClose={(e, reason) => { if (reason !== 'backdropClick') handleCloseOptions(); }}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                        <Button variant="outlined" color="primary" startIcon={<RefreshIcon />} onClick={clearFilters}>Show All Move-Outs</Button>
                        <Divider>OR</Divider>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Move-Out Status</InputLabel>
                                    <Select name="move_out_status" value={filters.move_out_status} onChange={handleFilterChange} label="Move-Out Status">
                                        <MenuItem value="">All Status</MenuItem>
                                        {moveOutStatusOptions.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Property</InputLabel>
                                    <Select name="property_id" value={filters.property_id} onChange={handleFilterChange} label="Property">
                                        <MenuItem value="">All Properties</MenuItem>
                                        {properties.map(property => <MenuItem key={property.id} value={property.id}>{property.property_name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Date From" name="date_from" type="date" value={filters.date_from} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Date To" name="date_to" type="date" value={filters.date_to} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseOptions} variant="contained">Close</Button>
                    <Button onClick={applyFilters} color="primary" variant="contained">Apply Filters</Button>
                </DialogActions>
            </Dialog>

            <Dialog 
                open={openAdd} 
                onClose={(e, reason) => { if (reason !== 'backdropClick') handleCloseAdd(); }} 
                TransitionComponent={Transition} 
                fullWidth 
                maxWidth="sm" 
                scroll="paper"
            >
                <DialogTitle sx={{ textAlign: 'center' }}><b>{postStatus === "ADD" ? "New Move-Out" : "Edit Move-Out"}</b></DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                        <Box>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}>Tenant Information</Typography>
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required>
                                    <InputLabel>Select Tenant *</InputLabel>
                                    <Select name="tenant_id" value={formData.tenant_id} onChange={handleInputChange} label="Select Tenant *">
                                        <MenuItem value="">Select a tenant</MenuItem>
                                        {activeTenants.map(tenant => (
                                            <MenuItem key={tenant.tenant_id || tenant.id} value={tenant.tenant_id || tenant.id}>
                                                {tenant.tenant_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Move-Out Date *" name="move_out_date" type="date" value={formData.move_out_date} onChange={handleInputChange} required InputLabelProps={{ shrink: true }} />
                            </Grid><br/>
                            <Grid item xs={12}>
                                <TextField fullWidth label="Forwarding Address" name="forwarding_address" value={formData.forwarding_address} onChange={handleInputChange} multiline rows={2} placeholder="Where to send deposit refund if applicable" />
                            </Grid><br/>
                            <Grid item xs={12}>
                                <FormControlLabel control={<Switch name="key_returned" checked={formData.key_returned} onChange={handleInputChange} color="primary" />} label="Keys Returned" />
                                {formData.key_returned && (
                                    <TextField label="Key Returned Date" name="key_returned_date" type="date" value={formData.key_returned_date} onChange={handleInputChange} sx={{ ml: 2, width: 200 }} InputLabelProps={{ shrink: true }} />
                                )}
                            </Grid><br/>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1, mt: 2 }}>Financial Calculations</Typography>
                            </Grid><br/>
                            <Grid item xs={12}>
                                <Button variant="outlined" color="primary" onClick={handleCalculate} startIcon={<CalculateIcon />} disabled={!formData.tenant_id}>
                                    Fetch Balances & Calculate
                                </Button>
                            </Grid><br/>
                            {calculatedData && (
                                <>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Last Month Rent Due" name="last_month_rent_due" type="number" value={formData.last_month_rent_due} onChange={handleInputChange} InputProps={{ startAdornment: <InputAdornment position="start">UGX</InputAdornment> }} />
                                    </Grid><br/>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Security Deposit Held" name="security_deposit_held" type="number" value={formData.security_deposit_held} onChange={handleInputChange} InputProps={{ startAdornment: <InputAdornment position="start">UGX</InputAdornment> }} />
                                    </Grid><br/>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Damage Charges" name="damage_charges" type="number" value={formData.damage_charges} onChange={handleInputChange} InputProps={{ startAdornment: <InputAdornment position="start"><ConstructionIcon /></InputAdornment> }} />
                                    </Grid><br/>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Cleaning Fee" name="cleaning_fee" type="number" value={formData.cleaning_fee} onChange={handleInputChange} InputProps={{ startAdornment: <InputAdornment position="start"><CleaningIcon /></InputAdornment> }} />
                                    </Grid><br/>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Repair Costs" name="repair_costs" type="number" value={formData.repair_costs} onChange={handleInputChange} InputProps={{ startAdornment: <InputAdornment position="start"><Build /></InputAdornment> }} />
                                    </Grid><br/>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Unpaid Rent Balance" name="unpaid_rent_balance" type="number" value={formData.unpaid_rent_balance} onChange={handleInputChange} InputProps={{ startAdornment: <InputAdornment position="start"><MoneyOff /></InputAdornment> }} />
                                    </Grid><br/>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Final Refund Amount (UGX)" name="final_amount_refund" type="number" value={formData.final_amount_refund} disabled InputProps={{ startAdornment: <InputAdornment position="start"><CurrencyExchange /></InputAdornment> }} />
                                    </Grid><br/>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Final Amount Owed by Tenant (UGX)" name="final_amount_owed_by_tenant" type="number" value={formData.final_amount_owed_by_tenant} disabled InputProps={{ startAdornment: <InputAdornment position="start"><MoneyOff /></InputAdornment> }} />
                                    </Grid><br/>
                                </>
                            )}
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Move-Out Status</InputLabel>
                                    <Select name="move_out_status" value={formData.move_out_status} onChange={handleInputChange} label="Move-Out Status">
                                        {moveOutStatusOptions.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid><br/>
                            <Grid item xs={12}>
                                <TextField fullWidth label="Notes" name="notes" value={formData.notes} onChange={handleInputChange} multiline rows={3} placeholder="Additional notes..." />
                            </Grid>
                        </Box>
                        <DialogActions sx={{ mt: 3, px: 0 }}>
                            <Button color="error" onClick={handleCloseAdd} variant="contained">Cancel</Button>
                            <Button type="submit" color="primary" variant="contained">{postStatus === "ADD" ? "Create Move-Out" : "Update Move-Out"}</Button>
                        </DialogActions>
                    </Box>
                </DialogContent>
            </Dialog>

            <Dialog 
                open={inspectionDialogOpen} 
                onClose={(e, reason) => { if (reason !== 'backdropClick') setInspectionDialogOpen(false); }} 
                TransitionComponent={Transition} 
                fullWidth 
                maxWidth="md"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Move-Out Inspection Checklist</DialogTitle>
                <DialogContent>
                    <Box sx={{ p: 2 }}>
                        <Typography variant="body1" gutterBottom>Property: <strong>{selectedRecord?.property_name}</strong></Typography>
                        <Alert severity="info" sx={{ mb: 2 }}>Check all items that are in good condition. Unchecked items will be considered for damage charges.</Alert>
                        <FormGroup>
                            <Grid container spacing={2}>
                                {inspectionChecklist.map((item) => (
                                    <Grid item xs={12} sm={6} key={item.id}>
                                        <FormControlLabel
                                            control={<Checkbox checked={inspectionItems.includes(item.id)} onChange={() => handleInspectionItemToggle(item.id)} />}
                                            label={item.label}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </FormGroup>
                        <TextField fullWidth label="Damage Charges (UGX)" type="number" value={damageCharges} onChange={(e) => setDamageCharges(e.target.value)} sx={{ mt: 2 }} InputProps={{ startAdornment: <InputAdornment position="start">UGX</InputAdornment> }} />
                        <TextField fullWidth label="Damage Notes" value={damageNotes} onChange={(e) => setDamageNotes(e.target.value)} multiline rows={3} sx={{ mt: 2 }} placeholder="Describe any damages found..." />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={() => setInspectionDialogOpen(false)} variant="contained">Cancel</Button>
                    <Button onClick={handleCompleteInspection} color="warning" variant="contained">Complete Inspection</Button>
                </DialogActions>
            </Dialog>

            <Dialog 
                open={generateDialogOpen} 
                onClose={(e, reason) => { if (reason !== 'backdropClick') setGenerateDialogOpen(false); }} 
                TransitionComponent={Transition}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Generate Final Statement</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>This will generate a final statement PDF that will be downloaded directly.</Alert>
                    <Typography variant="body2">Tenant: <strong>{selectedRecord?.tenant_name}</strong></Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>Security Deposit: {formatCurrency(selectedRecord?.security_deposit_held)}</Typography>
                    <Typography variant="body2">Damage Charges: {formatCurrency(selectedRecord?.damage_charges)}</Typography>
                    <Typography variant="body2">Unpaid Rent: {formatCurrency(selectedRecord?.unpaid_rent_balance)}</Typography>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="body1" fontWeight="bold">Final Refund: {formatCurrency(selectedRecord?.final_amount_refund)}</Typography>
                    <Typography variant="body1" fontWeight="bold" color="error">Amount Owed: {formatCurrency(selectedRecord?.final_amount_owed_by_tenant)}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={() => setGenerateDialogOpen(false)} variant="contained">Cancel</Button>
                    <Button onClick={handleGenerateStatement} color="primary" variant="contained">Generate & Download</Button>
                </DialogActions>
            </Dialog>

            <Dialog 
                open={closeDialogOpen} 
                onClose={(e, reason) => { if (reason !== 'backdropClick') setCloseDialogOpen(false); }} 
                TransitionComponent={Transition} 
                fullWidth 
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Close Move-Out</DialogTitle>
                <DialogContent>
                    <Box sx={{ p: 2 }}>
                        <Alert severity="success" sx={{ mb: 2 }}>Closing this move-out will mark it as completed.</Alert>
                        <TextField fullWidth label="Closing Notes" value={closeNotes} onChange={(e) => setCloseNotes(e.target.value)} multiline rows={3} placeholder="Final notes about the move-out process..." />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={() => setCloseDialogOpen(false)} variant="contained">Cancel</Button>
                    <Button onClick={handleClose} color="success" variant="contained">Close Move-Out</Button>
                </DialogActions>
            </Dialog>

            <Dialog 
                open={openView} 
                onClose={(e, reason) => { if (reason !== 'backdropClick') handleCloseView(); }} 
                TransitionComponent={Transition} 
                fullWidth 
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}><b>Move-Out Details</b></DialogTitle>
                <DialogContent>
                    {selectedRecord && (
                        <Box>
                            <Box>
                                <Grid item xs={12}><Typography variant="h6" color="primary">Move-Out Information</Typography></Grid><br/>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Move-Out ID:</b> {selectedRecord.move_out_id}</Typography></Grid><br/>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Status:</b> <Chip icon={getStatusIcon(selectedRecord.move_out_status)} label={getStatusText(selectedRecord.move_out_status)} color={getStatusColor(selectedRecord.move_out_status)} size="small" /></Typography></Grid><br/>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Move-Out Date:</b> {selectedRecord.move_out_date}</Typography></Grid><br/>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Keys Returned:</b> {selectedRecord.key_returned ? 'Yes' : 'No'}</Typography></Grid><br/>
                                {selectedRecord.key_returned_date && <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Keys Returned Date:</b> {selectedRecord.key_returned_date}</Typography></Grid>}<br/>
                                <Grid item xs={12}><Typography variant="h6" color="primary" sx={{ mt: 2 }}>Tenant & Property</Typography></Grid><br/>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Tenant:</b> {selectedRecord.tenant_name}</Typography></Grid><br/>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Phone:</b> {selectedRecord.tenant_phone}</Typography></Grid><br/>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Property:</b> {selectedRecord.property_name}</Typography></Grid><br/>
                                {selectedRecord.forwarding_address && <Grid item xs={12}><Typography variant="subtitle2"><b>Forwarding Address:</b> {selectedRecord.forwarding_address}</Typography></Grid>}<br/>
                                <Grid item xs={12}><Typography variant="h6" color="primary" sx={{ mt: 2 }}>Financial Summary</Typography></Grid><br/>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Security Deposit Held:</b> {formatCurrency(selectedRecord.security_deposit_held)}</Typography></Grid><br/>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Last Month Rent Due:</b> {formatCurrency(selectedRecord.last_month_rent_due)}</Typography></Grid><br/>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Damage Charges:</b> {formatCurrency(selectedRecord.damage_charges)}</Typography></Grid><br/>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Cleaning Fee:</b> {formatCurrency(selectedRecord.cleaning_fee)}</Typography></Grid><br/>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Repair Costs:</b> {formatCurrency(selectedRecord.repair_costs)}</Typography></Grid><br/>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Unpaid Rent Balance:</b> {formatCurrency(selectedRecord.unpaid_rent_balance)}</Typography></Grid><br/>
                                <Grid item xs={12}><Divider /></Grid>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2" fontWeight="bold"><b>Final Refund:</b> {formatCurrency(selectedRecord.final_amount_refund)}</Typography></Grid><br/>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2" fontWeight="bold" color="error"><b>Final Amount Owed:</b> {formatCurrency(selectedRecord.final_amount_owed_by_tenant)}</Typography></Grid><br/>
                                {selectedRecord.inspection_notes && <Grid item xs={12}><Typography variant="subtitle2"><b>Inspection Notes:</b> {selectedRecord.inspection_notes}</Typography></Grid>}<br/>
                                {selectedRecord.notes && <Grid item xs={12}><Typography variant="subtitle2"><b>Notes:</b> {selectedRecord.notes}</Typography></Grid>}<br/>
                                <Grid item xs={12}><Typography variant="h6" color="primary" sx={{ mt: 2 }}>System Information</Typography></Grid><br/>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Created:</b> {formatCreatedAtDate(selectedRecord.created_at)}</Typography></Grid><br/>
                                <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Last Updated:</b> {formatCreatedAtDate(selectedRecord.updated_at)}</Typography></Grid><br/>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseView} variant="contained">Close</Button>
                    {hasPermission('PRINT') && selectedRecord && (
                        <Button onClick={() => handlePrintStatement(selectedRecord)} color="info" variant="contained">
                            <PrintIcon sx={{ mr: 1 }} /> Print Statement
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog 
                open={openDelete} 
                onClose={(e, reason) => { if (reason !== 'backdropClick') handleCloseDelete(); }} 
                TransitionComponent={Transition}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Delete Move-Out Record</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>Are you sure you want to delete this move-out record?</Typography>
                    {selectedRecord && <Typography variant="h6" sx={{ textAlign: 'center' }}>{selectedRecord.tenant_name}</Typography>}
                    <Typography variant="body2" color="error" sx={{ textAlign: 'center', mt: 2 }}>Warning: This action cannot be undone!</Typography>
                </DialogContent>
                <DialogActions><Button color="error" onClick={handleCloseDelete} variant="contained">Cancel</Button><Button onClick={handleSubmit} color="primary" variant="contained">Confirm Delete</Button></DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
            </Snackbar>
        </Container>
    );
};

export default MoveOutChecklist;